import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import proj4 from 'proj4';
import { MapContainer, TileLayer, CircleMarker, useMapEvents, useMap, Marker, Popup, GeoJSON } from 'react-leaflet';
import { useLocation } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import api from '../services/api';
import 'leaflet.heat';
import ClusterGroup from './ClusterGroup';
import '../css/GISResponsive.css';

// Fix para icono de marcador en React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const getPointCoordinates = (point) => {
  if (Array.isArray(point?.coordinates) && point.coordinates.length === 2) {
    return point.coordinates;
  }

  const lat = Number(point?.Latitud ?? point?.latitud);
  const lng = Number(point?.Longitud ?? point?.longitud);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return [lat, lng];
  }

  try {
    const coords = JSON.parse(point.jsonSHAPE).coordinates;
    return [coords[1], coords[0]];
  } catch {
    return null;
  }
};

const getPointName = (point) => {
  const fullName = [point?.Apellido, point?.Nombre].filter(Boolean).join(', ');
  return point?.fna || point?.name || fullName || 'Sin nombre disponible';
};

const getPointAddress = (point) => (
  point?.DireccionCompleta
  || [point?.Calle, point?.Numero, point?.Piso, point?.Departamento].filter(Boolean).join(' ')
  || null
);

const getPointTimelineDate = (point, allowFallback = false) => {
  if (point?.fechaSeguimiento) return point.fechaSeguimiento;
  if (!allowFallback) return null;

  return (
    point?.FechaPrimeraConsulta
    || point?.FechaInicioSintomas
    || point?.FechaProbableExposicion
    || null
  );
};

const formatDateInput = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const addDays = (dateValue, days) => {
  const date = new Date(`${dateValue}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

const daysBetween = (startDate, endDate) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
};

const clampDate = (dateValue, minDate, maxDate) => {
  if (!dateValue || !minDate || !maxDate) return dateValue || maxDate || minDate || '';
  if (dateValue < minDate) return minDate;
  if (dateValue > maxDate) return maxDate;
  return dateValue;
};

const buildClinicalTrend = (cases, contacts, selectedDate) => {
  const rowsByDate = new Map();
  const addPoint = (point, type) => {
    const date = formatDateInput(getPointTimelineDate(point));
    if (!date) return;
    const row = rowsByDate.get(date) || { date, cases: 0, contacts: 0, total: 0 };
    if (type === 'case') row.cases += 1;
    if (type === 'contact') row.contacts += 1;
    row.total += 1;
    rowsByDate.set(date, row);
  };

  cases.forEach((point) => addPoint(point, 'case'));
  contacts.forEach((point) => addPoint(point, 'contact'));

  const series = Array.from(rowsByDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  const effectiveDate = selectedDate || series[series.length - 1]?.date || '';
  const visibleSeries = effectiveDate ? series.filter((row) => row.date <= effectiveDate) : series;
  const recentSeries = visibleSeries.slice(-7);
  const recentTotal = recentSeries.reduce((sum, row) => sum + row.total, 0);
  const totalCases = visibleSeries.reduce((sum, row) => sum + row.cases, 0);
  const totalContacts = visibleSeries.reduce((sum, row) => sum + row.contacts, 0);
  const maxDaily = Math.max(1, ...visibleSeries.map((row) => row.total));
  const peak = visibleSeries.reduce((best, row) => (row.total > best.total ? row : best), { date: '', total: 0 });

  return {
    series: visibleSeries.slice(-12),
    recentTotal,
    totalCases,
    totalContacts,
    maxDaily,
    peak,
  };
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const buildLayerPoints = (points, layerName, color) => (
  points.map((point) => ({ ...point, layerName, layerColor: color }))
);

const clinicalLayerStyles = {
  Casos: {
    className: 'gis-clinical-marker gis-clinical-marker-case',
    iconHtml: '!',
    color: '#7f1d1d',
    fillColor: '#dc2626',
    haloColor: 'rgba(220, 38, 38, 0.24)',
    label: 'Caso',
  },
  Contactos: {
    className: 'gis-clinical-marker gis-clinical-marker-contact',
    iconHtml: '+',
    color: '#991b1b',
    fillColor: '#f97316',
    haloColor: 'rgba(249, 115, 22, 0.22)',
    label: 'Contacto',
  },
};

const getClinicalLayerStyle = (layerName) => clinicalLayerStyles[layerName] || null;

const createClinicalIcon = (layerName) => {
  const style = getClinicalLayerStyle(layerName);
  if (!style) return null;

  return L.divIcon({
    className: style.className,
    html: `<span>${style.iconHtml}</span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
    tooltipAnchor: [10, 0],
  });
};

const createClinicalMarker = (point, coords, layerName) => {
  const icon = createClinicalIcon(layerName);
  if (!icon) return null;

  return L.marker(coords, { icon, riseOnHover: true, clinicalLayerName: layerName })
    .bindTooltip(`${layerName} - ${getPointName(point)}`)
    .bindPopup(getLeafletPopupHtml({ ...point, layerName }));
};

const popupContentStyle = {
  minWidth: 180,
  maxWidth: 260,
  lineHeight: 1.35,
};

const popupTitleStyle = {
  fontWeight: 700,
  color: '#1f2937',
  marginBottom: 6,
};

const popupLayerStyle = {
  fontSize: '0.85rem',
  color: '#4b5563',
};

const layerSwatchStyle = (color, borderColor = color) => ({
  display: 'inline-flex',
  width: 13,
  height: 13,
  borderRadius: '50%',
  background: color,
  border: `2px solid ${borderColor}`,
  marginRight: 7,
  verticalAlign: '-2px',
  boxShadow: '0 0 0 3px rgba(15,23,42,0.08)',
});

const HEALTH_LAYER_COLOR = '#059669';

const normalizePlaceName = (value) => String(value || "")
  .trim()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase();

const defaultLocalityCenters = {
  "mar del plata": [-38.0055, -57.5426],
  "mardel plata": [-38.0055, -57.5426],
};

const getKnownLocalityCenter = (zona) => {
  const keys = [
    zona?.Localidad,
    zona?.Nombre,
    zona?.JerarquiaZonaGeografica,
  ].map(normalizePlaceName);

  const key = keys.find((item) => defaultLocalityCenters[item]);
  return key ? defaultLocalityCenters[key] : null;
};

const buildZonaSearchQueries = (zona) => {
  if (!zona) return [];

  const localidad = zona.Localidad || "";
  const partido = zona.Partido || "";
  const provincia = zona.Provincia || "";
  const pais = zona.Pais || "Argentina";

  const queries = localidad
    ? [
      [localidad, partido, provincia, pais],
      [localidad, provincia, pais],
      [localidad, pais],
    ]
    : [
      [zona.Nombre, provincia, pais],
      [zona.Nombre, pais],
    ];

  return Array.from(new Set(
    queries
      .map((partes) => partes.filter(Boolean).join(", ").trim())
      .filter(Boolean)
      .map((texto) => texto.replace(/\bmardel\s+plata\b/i, "Mar del Plata"))
  ));
};

const buscarZonaEnArgentina = async (consultas) => {
  for (const consulta of consultas) {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(consulta)}&format=json&addressdetails=1&limit=3&countrycodes=ar`
    );
    const resultados = await res.json();
    const resultadoArgentina = Array.isArray(resultados)
      ? resultados.find((resultado) => resultado?.address?.country_code === "ar") || resultados[0]
      : null;

    if (resultadoArgentina?.lat && resultadoArgentina?.lon) {
      return resultadoArgentina;
    }
  }

  return null;
};

const completarZonaConCatalogo = async (zona) => {
  if (zona?.Localidad || !zona?.IdZonaSanitaria) return zona;

  try {
    const { data } = await api.get('/zonas-sanitarias');
    const zonaCatalogo = Array.isArray(data)
      ? data.find((item) => String(item.IdZonaSanitaria) === String(zona.IdZonaSanitaria))
      : null;

    return zonaCatalogo ? { ...zona, ...zonaCatalogo } : zona;
  } catch {
    return zona;
  }
};

const renderPointPopup = (point, layerName) => (
  <Popup>
    <div style={popupContentStyle}>
      <div style={popupTitleStyle}>{getPointName(point)}</div>
      <div style={popupLayerStyle}>Capa: {layerName}</div>
      {point?.NroDocumento && <div style={popupLayerStyle}>Documento: {point.NroDocumento}</div>}
      {point?.Edad && <div style={popupLayerStyle}>Edad: {point.Edad}</div>}
      {point?.Patologia && <div style={popupLayerStyle}>Patologia: {point.Patologia}</div>}
      {point?.ZonaSanitaria && <div style={popupLayerStyle}>Zona: {point.ZonaSanitaria}</div>}
      {getPointTimelineDate(point) && <div style={popupLayerStyle}>Fecha: {formatDateInput(getPointTimelineDate(point))}</div>}
      {getPointAddress(point) && <div style={popupLayerStyle}>Domicilio: {getPointAddress(point)}</div>}
    </div>
  </Popup>
);

const getLeafletPopupRow = (label, value) => (
  value ? `<div style="font-size:0.85rem;color:#4b5563;margin-top:3px"><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</div>` : ''
);

const getLeafletPopupHtml = (point) => `
  <div style="min-width:180px;max-width:260px;line-height:1.35">
    <div style="font-weight:700;color:#1f2937;margin-bottom:6px">${escapeHtml(getPointName(point))}</div>
    <div style="font-size:0.85rem;color:#4b5563">Capa: ${escapeHtml(point.layerName || 'Sin capa')}</div>
    ${getLeafletPopupRow('Documento', point.NroDocumento)}
    ${getLeafletPopupRow('Edad', point.Edad)}
    ${getLeafletPopupRow('Patologia', point.Patologia)}
    ${getLeafletPopupRow('Zona', point.ZonaSanitaria)}
    ${getLeafletPopupRow('Fecha', formatDateInput(getPointTimelineDate(point)))}
    ${getLeafletPopupRow('Domicilio', getPointAddress(point))}
  </div>
`;

const convertGeoJsonCoordinates = (coordinates) => {
  if (!Array.isArray(coordinates)) return coordinates;

  if (
    coordinates.length >= 2
    && typeof coordinates[0] === 'number'
    && typeof coordinates[1] === 'number'
  ) {
    if (Math.abs(coordinates[0]) > 180 || Math.abs(coordinates[1]) > 90) {
      return proj4('EPSG:3857', 'EPSG:4326', [coordinates[0], coordinates[1]]);
    }

    return coordinates;
  }

  return coordinates.map(convertGeoJsonCoordinates);
};

const parseGeoJsonFeature = (item, geometryField) => {
  try {
    const geometryValue = item?.[geometryField];
    const parsed = typeof geometryValue === 'string' ? JSON.parse(geometryValue) : geometryValue;
    const feature = parsed?.type === 'Feature'
      ? parsed
      : { type: 'Feature', geometry: parsed, properties: {} };

    if (!feature.geometry?.type || !feature.geometry?.coordinates) return null;

    return {
      ...feature,
      properties: { ...(feature.properties || {}), ...item },
      geometry: {
        ...feature.geometry,
        coordinates: convertGeoJsonCoordinates(feature.geometry.coordinates),
      },
    };
  } catch {
    return null;
  }
};

const getNbiFillColor = (value) => {
  const percent = Number(value);
  if (!Number.isFinite(percent)) return '#94a3b8';
  if (percent >= 30) return '#7f1d1d';
  if (percent >= 20) return '#dc2626';
  if (percent >= 10) return '#f97316';
  return '#facc15';
};

const renderAreaPopupHtml = (feature, layerName) => {
  const p = feature?.properties || {};
  const rows = layerName === 'NBI'
    ? [
      ['Personas con NBI', p.personas_con_nbi],
      ['Personas con NBI %', p.personas_con_nbi_porc],
      ['Total poblacion', p.total_pob],
      ['Hogares con NBI', p.hogares_con_nbi],
      ['Hogares con NBI %', p.hogares_con_nbi_porc],
      ['Total hogares', p.total_hog],
    ]
    : [
      ['Barrio', p.NombreBarrio],
      ['Provincia', p.NombreProvinciaBarrio || p.NombreProvincia],
      ['Departamento', p.NombreDepartamento],
      ['Poblacion total', p.PoblacionTotal],
      ['Total hogares', p.TotalHogares],
      ['Area', p.Area],
    ];

  return `
    <div style="min-width:190px;max-width:280px;line-height:1.35">
      <div style="font-weight:700;color:#1f2937;margin-bottom:6px">${escapeHtml(layerName)}</div>
      ${rows.map(([label, value]) => getLeafletPopupRow(label, value)).join('')}
    </div>
  `;
};

// Componente auxiliar para mover el mapa correctamente
function MapFlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && zoom && map) {
      map.setView(center, zoom, { animate: true });
    }
  }, [center, zoom, map]);
  return null;
}

// El CSS de clustering se importa globalmente en public/index.html

// Componente para renderizar el heatmap (debe estar fuera del componente principal)
function HeatmapLayer({ points }) {
  const map = useMap();
  const layerRef = useRef();

  React.useEffect(() => {
    if (!map) return;
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (points.length > 0) {
      // Cada punto: [lat, lng, intensidad]
      const heatPoints = points
        .map((p) => {
          const coords = getPointCoordinates(p);
          return coords ? [coords[0], coords[1], 0.82] : null;
        })
        .filter(Boolean);
      layerRef.current = L.heatLayer(heatPoints, {
        radius: 34,
        blur: 17,
        maxZoom: 17,
        minOpacity: 0.32,
        gradient: {
          0.25: '#fee2e2',
          0.45: '#fca5a5',
          0.65: '#ef4444',
          0.85: '#dc2626',
          1.0: '#7f1d1d',
        },
      });
      layerRef.current.addTo(map);
    }
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, points]);
  return null;
}

function GIS() {
  const location = useLocation();
      // Centrar y ajustar el zoom a todos los puntos de todas las capas activas
      const fitToActivePoints = () => {
        if (!mapRef.current) return;
        let allPoints = [];
        if (showSalud) allPoints = allPoints.concat(saludPoints);
        if (showUniversidades) allPoints = allPoints.concat(universidadPoints);
        if (showEducativos) allPoints = allPoints.concat(educativosPoints);
        if (showReligiosos) allPoints = allPoints.concat(religiososPoints);
        if (showOmnibus) allPoints = allPoints.concat(omnibusPoints);
        if (showGob) allPoints = allPoints.concat(gobPoints);
        if (showCientificos) allPoints = allPoints.concat(cientificosPoints);
        if (showDeportivos) allPoints = allPoints.concat(deportivosPoints);
        if (showCasos) allPoints = allPoints.concat(casosPoints);
        if (showContactos) allPoints = allPoints.concat(contactosPoints);
        const latlngs = allPoints.map(getPointCoordinates).filter(Boolean);
        const bounds = L.latLngBounds(latlngs);
        if (showNBI) {
          const nbiBounds = L.geoJSON(nbiFeatures).getBounds();
          if (nbiBounds.isValid()) bounds.extend(nbiBounds);
        }
        if (showAsentamientos) {
          const asentamientosBounds = L.geoJSON(asentamientosFeatures).getBounds();
          if (asentamientosBounds.isValid()) bounds.extend(asentamientosBounds);
        }
        if (!bounds.isValid()) {
          alert('No hay puntos para mostrar en las capas seleccionadas.');
          return;
        }
        mapRef.current.fitBounds(bounds, { padding: [40, 40] });
      };
    // Estados para búsqueda y asignación de persona
    const [NroDocumento, setNroDocumento] = useState("");
    const [nombre, setNombre] = useState("");
    const [apellido, setApellido] = useState("");
    const [edad, setEdad] = useState("");
    const [idPersona, setIdPersona] = useState("");
    const [tiposDomicilio, setTiposDomicilio] = useState([]);
    const [idTipoDomicilio, setIdTipoDomicilio] = useState("");
    const [tiposDomicilioError, setTiposDomicilioError] = useState("");
  // Estado para selección de domicilio
  const [selectedLatLng, setSelectedLatLng] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const puedeAsignarDomicilio = selectedLatLng && idPersona && idTipoDomicilio;

  const buildDomicilioPayload = () => ({
    idPersona: idPersona,
    arrayDomicilios: [{
      IdTipoDomicilio: Number(idTipoDomicilio),
      Calle: selectedAddress,
      Numero: selectedLatLng.lng,
      Piso: null,
      Departamento: null
    }],
    cantidadDomicilios: 1,
    IdZonaGeografica: null
  });

  useEffect(() => {
    const fetchTiposDomicilio = async () => {
      try {
        const response = await api.get('/tipos-domicilio');
        const tiposActivos = Array.isArray(response.data)
          ? response.data.filter((tipo) => Number(tipo.Estado) === 1)
          : [];
        setTiposDomicilio(tiposActivos);
        setTiposDomicilioError("");
      } catch {
        setTiposDomicilio([]);
        setTiposDomicilioError("No se pudieron cargar los tipos de domicilio");
      }
    };

    fetchTiposDomicilio();
  }, []);
  // Función para obtener dirección desde lat/lng (Nominatim)
  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      const data = await res.json();
      return data.display_name || '';
    } catch {
      return '';
    }
  };

  // Manejar click en el mapa para seleccionar domicilio
  function MapClickHandler() {
    useMapEvents({
      click: async (e) => {
        setSelectedLatLng(e.latlng);
        setSaveMsg("");
        const addr = await reverseGeocode(e.latlng.lat, e.latlng.lng);
        setSelectedAddress(addr);
      },
    });
    return null;
  }

  // Guardar domicilio en backend
  const handleSaveDomicilio = async () => {
    if (!selectedLatLng || !selectedAddress || !idPersona || !idTipoDomicilio) return;
    setSaving(true);
    setSaveMsg("");
    try {
      await api.post('/guardarDomiciliosPorPersonaGis', buildDomicilioPayload());
      setSaveMsg('¡Domicilio guardado exitosamente!');
    } catch {
      setSaveMsg('Error al guardar domicilio');
    }
    setSaving(false);
  };

  // Estado para buscador y resultados

  // Estado para buscador y resultados
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  // Estado para marcador de búsqueda
  const [searchMarker, setSearchMarker] = useState(null);
  const mapRef = useRef();
  // Estado para el centro y zoom del mapa
  const [mapCenter, setMapCenter] = useState([-34.6037, -58.3816]);
  const [mapZoom, setMapZoom] = useState(12);

  // Estado para overlays
  const [showSalud, setShowSalud] = useState(false);
  const [showUniversidades, setShowUniversidades] = useState(false);
  // Paneles: todos contraídos al inicio
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showClusters, setShowClusters] = useState(true);
  const [saludPoints, setSaludPoints] = useState([]);
  const [universidadPoints, setUniversidadPoints] = useState([]);
  const [educativosPoints, setEducativosPoints] = useState([]);
  const [religiososPoints, setReligiososPoints] = useState([]);
  const [omnibusPoints, setOmnibusPoints] = useState([]);
  const [gobPoints, setGobPoints] = useState([]);
  const [cientificosPoints, setCientificosPoints] = useState([]);
  const [deportivosPoints, setDeportivosPoints] = useState([]);
  const [casosAllPoints, setCasosAllPoints] = useState([]);
  const [contactosAllPoints, setContactosAllPoints] = useState([]);
  const [casosPoints, setCasosPoints] = useState([]);
  const [contactosPoints, setContactosPoints] = useState([]);
  const [nbiFeatures, setNbiFeatures] = useState([]);
  const [asentamientosFeatures, setAsentamientosFeatures] = useState([]);

  const [showEducativos, setShowEducativos] = useState(false);
  const [showReligiosos, setShowReligiosos] = useState(false);
  const [showOmnibus, setShowOmnibus] = useState(false);
  const [showGob, setShowGob] = useState(false);
  const [showCientificos, setShowCientificos] = useState(false);
  const [showDeportivos, setShowDeportivos] = useState(false);
  const [showCasos, setShowCasos] = useState(false);
  const [showContactos, setShowContactos] = useState(false);
  const [showNBI, setShowNBI] = useState(false);
  const [showAsentamientos, setShowAsentamientos] = useState(false);
  const [timelinePlaying, setTimelinePlaying] = useState(false);
  const [timelineSpeed, setTimelineSpeed] = useState(1);
  const [timelineTipoFecha, setTimelineTipoFecha] = useState('FechaPrimeraConsulta');
  const [timelineFechaInicial, setTimelineFechaInicial] = useState('');
  const [timelineFechaFinal, setTimelineFechaFinal] = useState('');
  const [timelineFechaSeleccionada, setTimelineFechaSeleccionada] = useState('');
  const [showTrendSummary, setShowTrendSummary] = useState(false);
  const timelineIntervalRef = useRef(null);

  // Función para cargar puntos desde el backend y convertir coordenadas si es necesario
  const fetchPoints = useCallback(async (endpoint, setPoints, extraParams = {}) => {
    console.log('[fetchPoints] llamado con endpoint:', endpoint, 'mapRef:', mapRef.current);
    if (!mapRef.current) return;
    const bounds = mapRef.current.getBounds();
    const southWest = bounds.getSouthWest();
    const northEast = bounds.getNorthEast();
    try {
      const res = await api.get(
        `/gis/${endpoint}`,
        {
          params: {
            southLat: southWest.lat,
            southLng: southWest.lng,
            northLat: northEast.lat,
            northLng: northEast.lng,
            ...extraParams,
          },
        }
      );
      // Si el backend devuelve coordenadas en Mercator, conviértelas a WGS84
      const mercatorProj = 'EPSG:3857';
      const wgs84Proj = 'EPSG:4326';
      const puntos = res.data.map((p) => {
        const lat = Number(p.Latitud ?? p.latitud);
        const lng = Number(p.Longitud ?? p.longitud);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { ...p, coordinates: [lat, lng] };
        }

        let coords = [];
        try {
          coords = JSON.parse(p.jsonSHAPE).coordinates;
        } catch {
          return null;
        }
        // Detectar si están en Mercator (valores grandes)
        if (coords && coords.length === 2 && Math.abs(coords[0]) > 180) {
          const latLng = proj4(mercatorProj, wgs84Proj, [coords[0], coords[1]]).reverse();
          return { ...p, coordinates: latLng };
        } else if (coords && coords.length === 2) {
          // Ya están en WGS84
          return { ...p, coordinates: [coords[1], coords[0]] };
        }
        return null;
      }).filter(Boolean);
      setPoints(puntos);
    } catch (e) {
      console.error(`[GIS] Error cargando capa ${endpoint}:`, e);
      setPoints([]);
    }
  }, []);

  const fetchGeoJsonFeatures = useCallback(async (endpoint, geometryField, setFeatures) => {
    if (!mapRef.current) return;
    const bounds = mapRef.current.getBounds();
    const southWest = bounds.getSouthWest();
    const northEast = bounds.getNorthEast();
    try {
      const res = await api.get(
        `/gis/${endpoint}`,
        {
          params: {
            southLat: southWest.lat,
            southLng: southWest.lng,
            northLat: northEast.lat,
            northLng: northEast.lng,
          },
        }
      );
      const features = (res.data || [])
        .map((item) => parseGeoJsonFeature(item, geometryField))
        .filter(Boolean);
      setFeatures(features);
    } catch (e) {
      console.error(`[GIS] Error cargando capa ${endpoint}:`, e);
      setFeatures([]);
    }
  }, []);

  const filterClinicalPointsByTimeline = useCallback((points) => {
    if (!timelineFechaSeleccionada) return points;

    return points.filter((point) => {
      const pointDate = formatDateInput(getPointTimelineDate(point));
      if (!pointDate) return true;
      return pointDate <= timelineFechaSeleccionada;
    });
  }, [timelineFechaSeleccionada]);

  useEffect(() => {
    const clinicalPoints = [...casosAllPoints, ...contactosAllPoints];
    const dates = clinicalPoints
      .map((point) => formatDateInput(getPointTimelineDate(point)))
      .filter(Boolean)
      .sort();

    if (!dates.length) {
      setTimelineFechaInicial('');
      setTimelineFechaFinal('');
      setTimelineFechaSeleccionada('');
      setTimelinePlaying(false);
      return;
    }

    const firstDate = dates[0];
    const lastDate = dates[dates.length - 1];
    setTimelineFechaInicial(firstDate);
    setTimelineFechaFinal(lastDate);
    setTimelineFechaSeleccionada((currentDate) => clampDate(currentDate || firstDate, firstDate, lastDate));
  }, [casosAllPoints, contactosAllPoints]);

  useEffect(() => {
    setCasosPoints(showCasos ? filterClinicalPointsByTimeline(casosAllPoints) : []);
    setContactosPoints(showContactos ? filterClinicalPointsByTimeline(contactosAllPoints) : []);
  }, [
    casosAllPoints,
    contactosAllPoints,
    filterClinicalPointsByTimeline,
    showCasos,
    showContactos,
  ]);

  useEffect(() => {
    if (timelinePlaying && timelineFechaSeleccionada && timelineFechaFinal) {
      timelineIntervalRef.current = setInterval(() => {
        setTimelineFechaSeleccionada((currentDate) => {
          const nextDate = addDays(currentDate, 1);
          if (nextDate > timelineFechaFinal) {
            setTimelinePlaying(false);
            return currentDate;
          }
          return nextDate;
        });
      }, 1000 / timelineSpeed);
    } else if (timelineIntervalRef.current) {
      clearInterval(timelineIntervalRef.current);
      timelineIntervalRef.current = null;
    }

    return () => {
      if (timelineIntervalRef.current) {
        clearInterval(timelineIntervalRef.current);
        timelineIntervalRef.current = null;
      }
    };
  }, [timelinePlaying, timelineSpeed, timelineFechaSeleccionada, timelineFechaFinal]);

  // Efectos para cargar puntos cuando se activa la capa
  React.useEffect(() => {
    if (showSalud) fetchPoints('getEdificiosDeSalud', setSaludPoints); else setSaludPoints([]);
  }, [showSalud, fetchPoints]);
  React.useEffect(() => {
    if (showUniversidades) fetchPoints('getUniversidades', setUniversidadPoints); else setUniversidadPoints([]);
  }, [showUniversidades, fetchPoints]);
  React.useEffect(() => {
    if (showEducativos) fetchPoints('getEstablecimientosEducativos', setEducativosPoints); else setEducativosPoints([]);
  }, [showEducativos, fetchPoints]);
  React.useEffect(() => {
    if (showReligiosos) fetchPoints('getEdificiosReligiosos', setReligiososPoints); else setReligiososPoints([]);
  }, [showReligiosos, fetchPoints]);
  React.useEffect(() => {
    if (showOmnibus) fetchPoints('getEstacionesDeOmnibus', setOmnibusPoints); else setOmnibusPoints([]);
  }, [showOmnibus, fetchPoints]);
  React.useEffect(() => {
    if (showGob) fetchPoints('getEdificiosGubernamentales', setGobPoints); else setGobPoints([]);
  }, [showGob, fetchPoints]);
  React.useEffect(() => {
    if (showCientificos) fetchPoints('getCentrosCientificos', setCientificosPoints); else setCientificosPoints([]);
  }, [showCientificos, fetchPoints]);
  React.useEffect(() => {
    if (showDeportivos) fetchPoints('getInstalacionDeportivaYDeEsparcimiento', setDeportivosPoints); else setDeportivosPoints([]);
  }, [showDeportivos, fetchPoints]);
  React.useEffect(() => {
    if (showCasos) fetchPoints('getCasos', setCasosAllPoints, { tipoFecha: timelineTipoFecha }); else setCasosAllPoints([]);
  }, [showCasos, fetchPoints, timelineTipoFecha]);
  React.useEffect(() => {
    if (showContactos) fetchPoints('getContactos', setContactosAllPoints, { tipoFecha: timelineTipoFecha }); else setContactosAllPoints([]);
  }, [showContactos, fetchPoints, timelineTipoFecha]);
  React.useEffect(() => {
    if (showNBI) fetchGeoJsonFeatures('getNBIMapa', 'jsonSHAPE', setNbiFeatures); else setNbiFeatures([]);
  }, [showNBI, fetchGeoJsonFeatures]);
  React.useEffect(() => {
    if (showAsentamientos) fetchGeoJsonFeatures('getAsentamientosMapa', 'Geometria', setAsentamientosFeatures); else setAsentamientosFeatures([]);
  }, [showAsentamientos, fetchGeoJsonFeatures]);

  // Hook para recargar puntos al mover el mapa
  function MapEvents() {
    useMapEvents({
      moveend: () => {
        if (showSalud) fetchPoints('getEdificiosDeSalud', setSaludPoints);
        if (showUniversidades) fetchPoints('getUniversidades', setUniversidadPoints);
        if (showEducativos) fetchPoints('getEstablecimientosEducativos', setEducativosPoints);
        if (showReligiosos) fetchPoints('getEdificiosReligiosos', setReligiososPoints);
        if (showOmnibus) fetchPoints('getEstacionesDeOmnibus', setOmnibusPoints);
        if (showGob) fetchPoints('getEdificiosGubernamentales', setGobPoints);
        if (showCientificos) fetchPoints('getCentrosCientificos', setCientificosPoints);
        if (showDeportivos) fetchPoints('getInstalacionDeportivaYDeEsparcimiento', setDeportivosPoints);
        if (showCasos) fetchPoints('getCasos', setCasosAllPoints, { tipoFecha: timelineTipoFecha });
        if (showContactos) fetchPoints('getContactos', setContactosAllPoints, { tipoFecha: timelineTipoFecha });
        if (showNBI) fetchGeoJsonFeatures('getNBIMapa', 'jsonSHAPE', setNbiFeatures);
        if (showAsentamientos) fetchGeoJsonFeatures('getAsentamientosMapa', 'Geometria', setAsentamientosFeatures);
      },
    });
    return null;
  }
  // Buscar dirección en Nominatim
  const handleSearch = async () => {
    if (!search.trim()) return;
    setShowDropdown(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(search)}&format=json&addressdetails=1&limit=5`);
      const data = await res.json();
      setResults(data);
    } catch (e) {
      setResults([]);
    }
  };

  // Centrar mapa al seleccionar resultado
  const handleSelectResult = (lat, lon, display_name) => {
    setShowDropdown(false);
    setSearch(display_name);
    setResults([]);
    setMapCenter([parseFloat(lat), parseFloat(lon)]);
    setMapZoom(16);
    // También actualizar dirección seleccionada y lat/lng
    setSelectedLatLng({ lat: parseFloat(lat), lng: parseFloat(lon) });
    setSelectedAddress(display_name);
    setSearchMarker([parseFloat(lat), parseFloat(lon)]);
  };

  useEffect(() => {
    const centrarEnZonaActiva = async () => {
      const idUsuario = localStorage.getItem("idUsuario");
      if (!idUsuario) return;

      try {
        const { data } = await api.get(`/usuarios/${idUsuario}/zonas-asignadas`);
        const zonas = Array.isArray(data) ? data : [];
        if (!zonas.length) return;

        const idZonaActiva = localStorage.getItem("idZonaActiva");
        const zonaSeleccionada =
          zonas.find((z) => Number(z.EsDefault) === 1) ||
          zonas.find((z) => String(z.IdZonaSanitaria) === String(idZonaActiva)) ||
          zonas[0];

        if (!zonaSeleccionada) return;

        const zona = await completarZonaConCatalogo(zonaSeleccionada);

        const lat = Number(zona.Latitud);
        const lng = Number(zona.Longitud);

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setMapCenter([lat, lng]);
          setMapZoom(13);
          return;
        }

        const knownCenter = getKnownLocalityCenter(zona);
        if (knownCenter) {
          setMapCenter(knownCenter);
          setMapZoom(13);
          return;
        }

        const consultas = buildZonaSearchQueries(zona);
        if (!consultas.length) return;

        const primerResultado = await buscarZonaEnArgentina(consultas);

        if (primerResultado?.lat && primerResultado?.lon) {
          setMapCenter([Number(primerResultado.lat), Number(primerResultado.lon)]);
          setMapZoom(13);
        }
      } catch (error) {
        console.error("No se pudo centrar GIS en la zona activa:", error);
      }
    };

    centrarEnZonaActiva();
  }, []);

  // Ya no es necesario el efecto aquí, lo maneja MapFlyTo

  // Estado para mostrar paneles flotantes
  const [showPanel, setShowPanel] = React.useState({ capas: false, buscador: false, persona: false });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const personaDesdeState = location.state?.persona || {};
    const debeAbrirPersona = location.state?.abrirPersonaDomicilio || params.get("panel") === "persona";
    const personaPrecargada = {
      IdPersona: personaDesdeState.IdPersona ?? params.get("idPersona"),
      NroDocumento: personaDesdeState.NroDocumento ?? params.get("nroDocumento"),
      Nombre: personaDesdeState.Nombre ?? params.get("nombre"),
      Apellido: personaDesdeState.Apellido ?? params.get("apellido"),
      Edad: personaDesdeState.Edad ?? params.get("edad"),
    };

    if (!debeAbrirPersona || !personaPrecargada.IdPersona) return;

    setShowPanel({ capas: false, buscador: false, persona: true });
    setIdPersona(personaPrecargada.IdPersona || "");
    setNroDocumento(personaPrecargada.NroDocumento || "");
    setNombre(personaPrecargada.Nombre || "");
    setApellido(personaPrecargada.Apellido || "");
    setEdad(personaPrecargada.Edad || "");
    setSelectedLatLng(null);
    setSelectedAddress("");
    setSearchMarker(null);
    setSaveMsg("");
  }, [location.search, location.state]);

  const timelineAvailable = showCasos || showContactos;
  const timelineMaxRangeValue = daysBetween(timelineFechaInicial, timelineFechaFinal);
  const timelineCurrentRangeValue = daysBetween(timelineFechaInicial, timelineFechaSeleccionada);
  const trendSummary = useMemo(() => buildClinicalTrend(
    showCasos ? casosAllPoints : [],
    showContactos ? contactosAllPoints : [],
    timelineFechaSeleccionada
  ), [showCasos, showContactos, casosAllPoints, contactosAllPoints, timelineFechaSeleccionada]);
  const handleTimelineRangeChange = (event) => {
    setTimelineFechaSeleccionada(addDays(timelineFechaInicial, Number(event.target.value)));
  };
  const handleTimelineStartChange = (event) => {
    const newDate = event.target.value;
    setTimelineFechaInicial(newDate);
    setTimelineFechaSeleccionada((currentDate) => clampDate(currentDate, newDate, timelineFechaFinal));
  };
  const handleTimelineEndChange = (event) => {
    const newDate = event.target.value;
    setTimelineFechaFinal(newDate);
    setTimelineFechaSeleccionada((currentDate) => clampDate(currentDate, timelineFechaInicial, newDate));
  };

  return (
    <div className="gis-page" style={{ width: '100vw', height: '100vh', minHeight: 400, position: 'relative', background: '#f6f8fa', display: 'flex', flexDirection: 'column' }}>

      {/* Botones de acordeón flotante */}
      <div className="gis-toolbar-item gis-toolbar-item--layers" style={{position:'absolute',top:24,left:54,zIndex:1100,display:'flex',flexDirection:'column',gap:10}}>
        <button className="gis-toolbar-button" onClick={()=>setShowPanel(p=>({...p,capas:!p.capas}))} style={{padding:'10px 22px',borderRadius:12,border:'none',background:showPanel.capas?'#007BFF':'#e0e7ef',color:showPanel.capas?'#fff':'#007BFF',fontWeight:700,fontSize:'1.08rem',boxShadow:'0 2px 8px 0 rgba(0,0,0,0.08)',cursor:'pointer'}}>Capas del mapa</button>
      </div>
      <div className="gis-toolbar-item gis-toolbar-item--search" style={{position:'absolute',top:24,left:'50%',transform:'translateX(-50%)',zIndex:1100}}>
        <button className="gis-toolbar-button" onClick={()=>setShowPanel(p=>({...p,buscador:!p.buscador}))} style={{padding:'10px 22px',borderRadius:12,border:'none',background:showPanel.buscador?'#007BFF':'#e0e7ef',color:showPanel.buscador?'#fff':'#007BFF',fontWeight:700,fontSize:'1.08rem',boxShadow:'0 2px 8px 0 rgba(0,0,0,0.08)',cursor:'pointer'}}>Buscador</button>
      </div>
      <div className="gis-toolbar-item gis-toolbar-item--person" style={{position:'absolute',top:24,right:24,zIndex:1100}}>
        <button className="gis-toolbar-button" onClick={()=>setShowPanel(p=>({...p,persona:!p.persona}))} style={{padding:'10px 22px',borderRadius:12,border:'none',background:showPanel.persona?'#007BFF':'#e0e7ef',color:showPanel.persona?'#fff':'#007BFF',fontWeight:700,fontSize:'1.08rem',boxShadow:'0 2px 8px 0 rgba(0,0,0,0.08)',cursor:'pointer'}}>Persona/Domicilio</button>
      </div>

      {/* Panel flotante izquierdo: Capas del mapa */}
      {showPanel.capas && (
        <div className="gis-floating-panel gis-floating-panel--layers" style={{
          position: 'absolute',
          top: 70,
          left: 24,
          zIndex: 1000,
          width: 320,
          background: 'linear-gradient(120deg, #f8fafc 60%, #e0e7ef 100%)',
          borderRadius: 18,
          boxShadow: '0 6px 24px 0 rgba(0,0,0,0.10)',
          padding: '18px 24px 18px 24px',
          border: '1.5px solid #e5e7eb',
          maxHeight: '80vh',
          minWidth: 180,
          overflow:'auto'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label>
              <input type="checkbox" checked={showHeatmap} onChange={e => setShowHeatmap(e.target.checked)} /> Mapa de calor
            </label>
            <label>
              <input type="checkbox" checked={showClusters} onChange={e => setShowClusters(e.target.checked)} /> Agrupar puntos (clustering)
            </label>
            <label>
              <input type="checkbox" checked={showCasos} onChange={e => setShowCasos(e.target.checked)} /> <span style={layerSwatchStyle('#dc2626', '#7f1d1d')} /> Casos
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{casosPoints.length > 0 ? `(${casosPoints.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showContactos} onChange={e => setShowContactos(e.target.checked)} /> <span style={layerSwatchStyle('#f97316', '#991b1b')} /> Contactos
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{contactosPoints.length > 0 ? `(${contactosPoints.length})` : ''}</span>
            </label>
            {timelineAvailable && (
              <div style={{
                margin: '2px 0 8px 0',
                padding: 12,
                borderRadius: 12,
                border: '1px solid #cbd5e1',
                background: 'rgba(255,255,255,0.82)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
              }}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:8}}>
                  <button
                    type="button"
                    onClick={() => setTimelinePlaying((playing) => !playing)}
                    disabled={!timelineFechaSeleccionada || timelineCurrentRangeValue >= timelineMaxRangeValue}
                    style={{
                      width: 36,
                      height: 32,
                      borderRadius: 10,
                      border: 'none',
                      background: timelinePlaying ? '#16a34a' : '#2563eb',
                      color: '#fff',
                      fontWeight: 800,
                      cursor: (!timelineFechaSeleccionada || timelineCurrentRangeValue >= timelineMaxRangeValue) ? 'not-allowed' : 'pointer',
                      opacity: (!timelineFechaSeleccionada || timelineCurrentRangeValue >= timelineMaxRangeValue) ? 0.55 : 1,
                    }}
                    title={timelinePlaying ? 'Pausar timeline' : 'Reproducir timeline'}
                  >
                    {timelinePlaying ? 'II' : '>'}
                  </button>
                  <div style={{display:'flex',gap:6}}>
                    {[1, 2, 5].map((speed) => (
                      <button
                        key={speed}
                        type="button"
                        onClick={() => setTimelineSpeed(speed)}
                        style={{
                          padding: '5px 9px',
                          borderRadius: 999,
                          border: timelineSpeed === speed ? '1px solid #2563eb' : '1px solid #cbd5e1',
                          background: timelineSpeed === speed ? '#dbeafe' : '#fff',
                          color: timelineSpeed === speed ? '#1d4ed8' : '#475569',
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: '0.78rem',
                        }}
                      >
                        {speed}x
                      </button>
                    ))}
                  </div>
                  <span style={{
                    padding: '6px 9px',
                    borderRadius: 999,
                    background: '#eef2ff',
                    color: '#3730a3',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    whiteSpace: 'nowrap',
                  }}>
                    {timelineFechaSeleccionada || 'Sin fecha'}
                  </span>
                </div>
                <select
                  value={timelineTipoFecha}
                  onChange={(event) => setTimelineTipoFecha(event.target.value)}
                  style={{
                    width: '100%',
                    padding: '7px 9px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    marginBottom: 8,
                    fontSize: '0.86rem',
                    background: '#fff',
                  }}
                >
                  <option value="FechaPrimeraConsulta">Primera consulta</option>
                  <option value="FechaInicioSintomas">Inicio sintomas</option>
                  <option value="FechaProbableExposicion">Probable exposicion</option>
                </select>
                <input
                  type="range"
                  min="0"
                  max={timelineMaxRangeValue}
                  value={timelineCurrentRangeValue}
                  onChange={handleTimelineRangeChange}
                  disabled={!timelineFechaInicial || !timelineFechaFinal}
                  style={{width:'100%',accentColor:'#2563eb'}}
                />
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:8}}>
                  <input
                    type="date"
                    value={timelineFechaInicial}
                    onChange={handleTimelineStartChange}
                    disabled={!timelineFechaInicial}
                    style={{minWidth:0,padding:'6px 7px',borderRadius:8,border:'1px solid #cbd5e1',fontSize:'0.78rem'}}
                  />
                  <input
                    type="date"
                    value={timelineFechaFinal}
                    onChange={handleTimelineEndChange}
                    disabled={!timelineFechaFinal}
                    style={{minWidth:0,padding:'6px 7px',borderRadius:8,border:'1px solid #cbd5e1',fontSize:'0.78rem'}}
                  />
                </div>
                <div style={{marginTop:6,fontSize:'0.78rem',color:'#64748b',fontWeight:600}}>
                  Visibles: {casosPoints.length + contactosPoints.length} de {casosAllPoints.length + contactosAllPoints.length}
                </div>
                <button
                  type="button"
                  onClick={() => setShowTrendSummary((value) => !value)}
                  style={{
                    width: '100%',
                    marginTop: 8,
                    padding: '7px 9px',
                    borderRadius: 9,
                    border: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    color: '#334155',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>Tendencia</span>
                  <span style={{color:'#64748b',fontWeight:600}}>
                    {trendSummary.recentTotal} ult. 7 fechas {showTrendSummary ? '-' : '+'}
                  </span>
                </button>
                {showTrendSummary && (
                  <div style={{
                    marginTop: 8,
                    padding: '8px 9px',
                    borderRadius: 10,
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                  }}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:8}}>
                      <div style={{fontSize:'0.78rem',color:'#475569'}}>
                        Casos <strong style={{color:'#b91c1c'}}>{trendSummary.totalCases}</strong>
                      </div>
                      <div style={{fontSize:'0.78rem',color:'#475569'}}>
                        Contactos <strong style={{color:'#c2410c'}}>{trendSummary.totalContacts}</strong>
                      </div>
                    </div>
                    <div style={{display:'flex',alignItems:'flex-end',gap:4,height:42}}>
                      {trendSummary.series.length > 0 ? trendSummary.series.map((row) => (
                        <div
                          key={row.date}
                          title={`${row.date}: ${row.total}`}
                          style={{
                            flex: 1,
                            minWidth: 6,
                            height: `${Math.max(10, (row.total / trendSummary.maxDaily) * 42)}px`,
                            borderRadius: '5px 5px 2px 2px',
                            background: 'linear-gradient(180deg, #ef4444 0%, #991b1b 100%)',
                            opacity: 0.82,
                          }}
                        />
                      )) : (
                        <span style={{fontSize:'0.78rem',color:'#94a3b8'}}>Sin fechas para calcular tendencia</span>
                      )}
                    </div>
                    {trendSummary.peak.date && (
                      <div style={{marginTop:7,fontSize:'0.74rem',color:'#64748b'}}>
                        Pico: {trendSummary.peak.total} en {trendSummary.peak.date}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <label>
              <input type="checkbox" checked={showNBI} onChange={e => setShowNBI(e.target.checked)} /> NBI
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{nbiFeatures.length > 0 ? `(${nbiFeatures.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showAsentamientos} onChange={e => setShowAsentamientos(e.target.checked)} /> Asentamientos
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{asentamientosFeatures.length > 0 ? `(${asentamientosFeatures.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showSalud} onChange={e => setShowSalud(e.target.checked)} /> Edificios de Salud
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{saludPoints.length > 0 ? `(${saludPoints.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showUniversidades} onChange={e => setShowUniversidades(e.target.checked)} /> Universidades
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{universidadPoints.length > 0 ? `(${universidadPoints.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showEducativos} onChange={e => setShowEducativos(e.target.checked)} /> Establecimientos Educativos
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{educativosPoints.length > 0 ? `(${educativosPoints.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showReligiosos} onChange={e => setShowReligiosos(e.target.checked)} /> Edificios Religiosos
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{religiososPoints.length > 0 ? `(${religiososPoints.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showOmnibus} onChange={e => setShowOmnibus(e.target.checked)} /> Estaciones de Ómnibus
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{omnibusPoints.length > 0 ? `(${omnibusPoints.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showGob} onChange={e => setShowGob(e.target.checked)} /> Edificios Gubernamentales
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{gobPoints.length > 0 ? `(${gobPoints.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showCientificos} onChange={e => setShowCientificos(e.target.checked)} /> Centros Científicos
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{cientificosPoints.length > 0 ? `(${cientificosPoints.length})` : ''}</span>
            </label>
            <label>
              <input type="checkbox" checked={showDeportivos} onChange={e => setShowDeportivos(e.target.checked)} /> Instalaciones Deportivas
              <span style={{color:'#888',fontSize:'0.95em',marginLeft:6}}>{deportivosPoints.length > 0 ? `(${deportivosPoints.length})` : ''}</span>
            </label>
            <button
              style={{marginTop:12, padding:'8px 18px', borderRadius:8, border:'none', background:'#007BFF', color:'#fff', fontWeight:600, cursor:'pointer', fontSize:'0.98em', opacity: ((showCasos && casosPoints.length) || (showContactos && contactosPoints.length) || (showNBI && nbiFeatures.length) || (showAsentamientos && asentamientosFeatures.length) || (showSalud && saludPoints.length) || (showUniversidades && universidadPoints.length) || (showEducativos && educativosPoints.length) || (showReligiosos && religiososPoints.length) || (showOmnibus && omnibusPoints.length) || (showGob && gobPoints.length) || (showCientificos && cientificosPoints.length) || (showDeportivos && deportivosPoints.length)) ? 1 : 0.5}}
              onClick={fitToActivePoints}
              disabled={!(showCasos && casosPoints.length) && !(showContactos && contactosPoints.length) && !(showNBI && nbiFeatures.length) && !(showAsentamientos && asentamientosFeatures.length) && !(showSalud && saludPoints.length) && !(showUniversidades && universidadPoints.length) && !(showEducativos && educativosPoints.length) && !(showReligiosos && religiososPoints.length) && !(showOmnibus && omnibusPoints.length) && !(showGob && gobPoints.length) && !(showCientificos && cientificosPoints.length) && !(showDeportivos && deportivosPoints.length)}
            >Centrar y mostrar capas activas</button>
          </div>
        </div>
      )}

      {/* Panel flotante superior: Buscador */}
      {showPanel.buscador && (
        <div className="gis-floating-panel gis-floating-panel--search" style={{
          position: 'absolute',
          top: 70,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)',
          padding: '14px 24px',
          minWidth: 180,
          maxWidth: '90vw',
          width: 'min(90vw, 420px)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <input
            type="text"
            placeholder="Buscar dirección o lugar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => search && results.length > 0 && setShowDropdown(true)}
            style={{
              padding: '10px 16px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
              outline: 'none',
              width: 220,
              marginRight: 8,
            }}
          />
          <button
            style={{
              padding: '10px 22px',
              background: 'linear-gradient(90deg, #007BFF 60%, #0056b3 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: '0 1px 2px 0 rgba(0,0,0,0.04)',
            }}
            onClick={handleSearch}
          >Buscar</button>
          {/* Dropdown de resultados */}
          {showDropdown && results.length > 0 && (
            <div style={{
              position: 'absolute',
              top: 54,
              left: 0,
              width: '100%',
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)',
              zIndex: 2000,
              maxHeight: 260,
              overflowY: 'auto',
            }}>
              {results.map((r, i) => (
                <div
                  key={i}
                  style={{ padding: 10, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '0.98rem' }}
                  onClick={() => handleSelectResult(r.lat, r.lon, r.display_name)}
                  onMouseDown={e => e.preventDefault()}
                >
                  {r.display_name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Panel flotante derecho: Persona/Domicilio */}
      {showPanel.persona && (
        <div className="gis-floating-panel gis-floating-panel--person" style={{
          position: 'absolute',
          top: 70,
          right: 24,
          zIndex: 1000,
          width: 'min(95vw, 400px)',
          background: 'linear-gradient(120deg, #f8fafc 60%, #e0e7ef 100%)',
          borderRadius: 22,
          boxShadow: '0 8px 36px 0 rgba(0,0,0,0.13)',
          padding: '18px 28px',
          minHeight: 120,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1.5px solid #e5e7eb',
          overflow: 'auto',
        }}>
          <label style={{ fontWeight: 500 }}>Documento:</label>
          <input
            type="text"
            placeholder="Nro. Documento"
            value={NroDocumento}
            onChange={e => setNroDocumento(e.target.value.replace(/[^0-9]/g, ""))}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
              outline: 'none',
              width: 180,
              marginBottom: 4,
            }}
          />
          <button
            style={{
              padding: '10px 18px',
              background: 'linear-gradient(90deg, #007BFF 60%, #0056b3 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '1rem',
              marginTop: 2,
              width: 120,
              alignSelf: 'flex-start',
            }}
            onClick={async () => {
              try {
                const res = await api.get(`/buscarPersonaPorNroDocumento/${NroDocumento}`);
                if (res.data && res.data.IdPersona) {
                  setNombre(res.data.Nombre);
                  setApellido(res.data.Apellido);
                  setEdad(res.data.Edad);
                  setIdPersona(res.data.IdPersona);
                } else {
                  setNombre(''); setApellido(''); setEdad(''); setIdPersona('');
                }
              } catch {
                setNombre(''); setApellido(''); setEdad(''); setIdPersona('');
              }
            }}
          >Buscar</button>
          <label style={{ fontWeight: 500 }}>Nombre:</label>
          <span style={{ color: '#333', fontSize: '1rem' }}>{nombre || '-'}</span>
          <label style={{ fontWeight: 500, marginTop: 4 }}>Apellido:</label>
          <span style={{ color: '#333', fontSize: '1rem' }}>{apellido || '-'}</span>
          <label style={{ fontWeight: 500, marginTop: 4 }}>Edad:</label>
          <span style={{ color: '#333', fontSize: '1rem' }}>{edad || '-'}</span>
          <label style={{ fontWeight: 500, marginTop: 8 }}>Tipo de domicilio:</label>
          <select
            value={idTipoDomicilio}
            onChange={e => setIdTipoDomicilio(e.target.value)}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
              outline: 'none',
              width: '100%',
              marginBottom: 4,
              background: '#fff',
            }}
          >
            <option value="">Seleccionar tipo</option>
            {tiposDomicilio.map((tipo) => (
              <option key={tipo.IdTipoDomicilio} value={tipo.IdTipoDomicilio}>
                {tipo.Nombre}
              </option>
            ))}
          </select>
          {tiposDomicilioError && (
            <span style={{ color: '#e74c3c', fontSize: '0.9rem' }}>{tiposDomicilioError}</span>
          )}
          <div style={{ marginTop: 8, color: '#007BFF', fontWeight: 600, fontSize: '1.05rem' }}>
            {selectedLatLng ? (
              <>
                <span style={{ color: '#222', fontWeight: 500 }}>Dirección seleccionada:</span><br/>
                <span style={{ color: '#007BFF' }}>{selectedAddress || `${selectedLatLng.lat.toFixed(6)}, ${selectedLatLng.lng.toFixed(6)}`}</span>
              </>
            ) : (
              <span style={{ color: '#888', fontWeight: 400 }}>Haz click en el mapa para seleccionar domicilio</span>
            )}
          </div>
          <button
            style={{
              padding: '12px 28px',
              background: puedeAsignarDomicilio ? 'linear-gradient(90deg, #28A745 60%, #218838 100%)' : '#e5e7eb',
              color: puedeAsignarDomicilio ? '#fff' : '#888',
              border: 'none',
              borderRadius: 10,
              cursor: puedeAsignarDomicilio ? 'pointer' : 'not-allowed',
              fontWeight: 700,
              fontSize: '1.1rem',
              marginTop: 10,
              boxShadow: '0 2px 8px 0 rgba(0,0,0,0.07)',
              letterSpacing: '1px',
              transition: 'background 0.2s',
            }}
            onClick={async () => {
              if (!selectedLatLng || !selectedAddress || !idPersona || !idTipoDomicilio) return;
              setSaving(true);
              setSaveMsg("");
              try {
                await api.post('/guardarDomiciliosPorPersonaGis', {
                  idPersona: idPersona,
                  arrayDomicilios: [{
                    IdTipoDomicilio: Number(idTipoDomicilio),
                    Calle: selectedAddress,
                    Numero: selectedLatLng.lng, // Ajusta si tienes el número real
                    Piso: null,
                    Departamento: null
                  }],
                  cantidadDomicilios: 1,
                  IdZonaGeografica: null
                });
                setSaveMsg('¡Domicilio guardado exitosamente!');
              } catch {
                setSaveMsg('Error al guardar domicilio');
              }
              setSaving(false);
            }}
            disabled={!puedeAsignarDomicilio || saving}
          >{saving ? 'Guardando...' : 'Asignar domicilio'}</button>
          {saveMsg && <div style={{marginTop:10, color: saveMsg.includes('Error') ? '#e74c3c' : '#27ae60', fontWeight:600}}>{saveMsg}</div>}
        </div>
      )}

      {/* Mapa base (restaurado) */}
      <MapContainer
        ref={mapRef}
        center={mapCenter}
        zoom={mapZoom}
        style={{ width: '100%', height: '100%', minHeight: 300, borderRadius: 18, boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)' }}
      >
        <MapFlyTo center={mapCenter} zoom={mapZoom} />
      
        {/* TileLayer IGN XYZ, compatible con Leaflet */}
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        {/* Marcador de búsqueda de dirección */}
        {searchMarker && (
          <Marker position={searchMarker} />
        )}
        {/* Overlays dinámicos */}
        {showNBI && nbiFeatures.length > 0 && (
          <GeoJSON
            key={`nbi-${nbiFeatures.length}`}
            data={nbiFeatures}
            style={(feature) => ({
              color: '#7c2d12',
              weight: 1,
              fillColor: getNbiFillColor(feature?.properties?.personas_con_nbi_porc),
              fillOpacity: 0.32,
            })}
            onEachFeature={(feature, layer) => {
              layer.bindPopup(renderAreaPopupHtml(feature, 'NBI'));
            }}
          />
        )}
        {showAsentamientos && asentamientosFeatures.length > 0 && (
          <GeoJSON
            key={`asentamientos-${asentamientosFeatures.length}`}
            data={asentamientosFeatures}
            style={{
              color: '#0f766e',
              weight: 2,
              fillColor: '#14b8a6',
              fillOpacity: 0.28,
            }}
            onEachFeature={(feature, layer) => {
              layer.bindPopup(renderAreaPopupHtml(feature, 'Asentamientos'));
            }}
          />
        )}
        {/* Heatmap overlay */}
        {showHeatmap && (
          <HeatmapLayer
            points={[
              ...(showCasos ? casosPoints : []),
              ...(showContactos ? contactosPoints : []),
            ]}
          />
        )}
        {/* Clustering de puntos usando ClusterGroup */}
        {showClusters && (
          <ClusterGroup
            points={[
              ...(showCasos ? buildLayerPoints(casosPoints, 'Casos', '#dc2626') : []),
              ...(showContactos ? buildLayerPoints(contactosPoints, 'Contactos', '#2563eb') : []),
              ...(showSalud ? buildLayerPoints(saludPoints, 'Edificios de Salud', HEALTH_LAYER_COLOR) : []),
              ...(showUniversidades ? buildLayerPoints(universidadPoints, 'Universidades', '#2980b9') : []),
              ...(showEducativos ? buildLayerPoints(educativosPoints, 'Establecimientos Educativos', '#f1c40f') : []),
              ...(showReligiosos ? buildLayerPoints(religiososPoints, 'Edificios Religiosos', '#8e44ad') : []),
              ...(showOmnibus ? buildLayerPoints(omnibusPoints, 'Estaciones de Omnibus', '#16a085') : []),
              ...(showGob ? buildLayerPoints(gobPoints, 'Edificios Gubernamentales', '#34495e') : []),
              ...(showCientificos ? buildLayerPoints(cientificosPoints, 'Centros Cientificos', '#e67e22') : []),
              ...(showDeportivos ? buildLayerPoints(deportivosPoints, 'Instalaciones Deportivas', '#27ae60') : []),
            ]}
            renderMarker={(p, i) => {
              const coords = getPointCoordinates(p);
              if (!coords) return null;

              if (getClinicalLayerStyle(p.layerName)) {
                return createClinicalMarker(p, coords, p.layerName);
              }

              return L.circleMarker(coords, {
                radius: 8,
                color: p.layerColor || '#007BFF',
                fillColor: p.layerColor || '#007BFF',
                fillOpacity: 0.7,
                layerName: p.layerName,
              })
                .bindTooltip(getPointName(p))
                .bindPopup(getLeafletPopupHtml(p));
            }}
          />
        )}
        {/* Si clustering desactivado, mostrar puntos planos */}
        {!showClusters && (
          <>
            {showCasos && casosPoints.map((p, i) => {
              const coords = getPointCoordinates(p);
              if (!coords) return null;
              return (
                <Marker
                  key={`caso-${p.IdPersona || i}-${p.IdDomicilio || i}`}
                  position={coords}
                  icon={createClinicalIcon('Casos')}
                >
                  {renderPointPopup(p, 'Casos')}
                </Marker>
              );
            })}
            {showContactos && contactosPoints.map((p, i) => {
              const coords = getPointCoordinates(p);
              if (!coords) return null;
              return (
                <Marker
                  key={`contacto-${p.IdPersona || i}-${p.IdDomicilio || i}`}
                  position={coords}
                  icon={createClinicalIcon('Contactos')}
                >
                  {renderPointPopup(p, 'Contactos')}
                </Marker>
              );
            })}
            {showSalud && saludPoints.map((p, i) => {
              if (!p.coordinates) return null;
              return (
                <CircleMarker
                  key={p.id || i}
                  center={p.coordinates}
                  radius={8}
                  color={HEALTH_LAYER_COLOR}
                  fillColor={HEALTH_LAYER_COLOR}
                  fillOpacity={0.7}
                >
                  {renderPointPopup(p, 'Edificios de Salud')}
                </CircleMarker>
              );
            })}
            {showUniversidades && universidadPoints.map((p, i) => {
              if (!p.coordinates) return null;
              return (
                <CircleMarker
                  key={p.id || i}
                  center={p.coordinates}
                  radius={8}
                  color="#2980b9"
                  fillColor="#2980b9"
                  fillOpacity={0.7}
                >
                  {renderPointPopup(p, 'Universidades')}
                </CircleMarker>
              );
            })}
            {showEducativos && educativosPoints.map((p, i) => {
              if (!p.coordinates) return null;
              return (
                <CircleMarker
                  key={p.id || i}
                  center={p.coordinates}
                  radius={7}
                  color="#f1c40f"
                  fillColor="#f1c40f"
                  fillOpacity={0.7}
                >
                  {renderPointPopup(p, 'Establecimientos Educativos')}
                </CircleMarker>
              );
            })}
            {showReligiosos && religiososPoints.map((p, i) => {
              if (!p.coordinates) return null;
              return (
                <CircleMarker
                  key={p.id || i}
                  center={p.coordinates}
                  radius={7}
                  color="#8e44ad"
                  fillColor="#8e44ad"
                  fillOpacity={0.7}
                >
                  {renderPointPopup(p, 'Edificios Religiosos')}
                </CircleMarker>
              );
            })}
            {showOmnibus && omnibusPoints.map((p, i) => {
              if (!p.coordinates) return null;
              return (
                <CircleMarker
                  key={p.id || i}
                  center={p.coordinates}
                  radius={7}
                  color="#16a085"
                  fillColor="#16a085"
                  fillOpacity={0.7}
                >
                  {renderPointPopup(p, 'Estaciones de Omnibus')}
                </CircleMarker>
              );
            })}
            {showGob && gobPoints.map((p, i) => {
              if (!p.coordinates) return null;
              return (
                <CircleMarker
                  key={p.id || i}
                  center={p.coordinates}
                  radius={7}
                  color="#34495e"
                  fillColor="#34495e"
                  fillOpacity={0.7}
                >
                  {renderPointPopup(p, 'Edificios Gubernamentales')}
                </CircleMarker>
              );
            })}
            {showCientificos && cientificosPoints.map((p, i) => {
              if (!p.coordinates) return null;
              return (
                <CircleMarker
                  key={p.id || i}
                  center={p.coordinates}
                  radius={7}
                  color="#e67e22"
                  fillColor="#e67e22"
                  fillOpacity={0.7}
                >
                  {renderPointPopup(p, 'Centros Cientificos')}
                </CircleMarker>
              );
            })}
            {showDeportivos && deportivosPoints.map((p, i) => {
              if (!p.coordinates) return null;
              return (
                <CircleMarker
                  key={p.id || i}
                  center={p.coordinates}
                  radius={7}
                  color="#27ae60"
                  fillColor="#27ae60"
                  fillOpacity={0.7}
                >
                  {renderPointPopup(p, 'Instalaciones Deportivas')}
                </CircleMarker>
              );
            })}
          </>
        )}
        <MapClickHandler />
        <MapEvents />
      </MapContainer>
    </div>
  );
}

export default GIS;


